// The facility Observaciones list (/observaciones) as data: its URL
// filters, the row model, the plazo situation of an entry and the
// «críticas sin atender» predicate the overview tile counts and links to.
// Mechanics in ./listState.ts.
//
// Every state read goes through the observation thread (entryThread.ts,
// ADR-0134): the row's kind comes from `entryState`, never from comparing
// recipe status keys (fitness observation-phase-single-source).

import type {
  FacilityDashboardAsset,
  FacilityDashboardEntry,
  ObservationPhaseVocabulary,
  RecipeMeResponse,
} from "@bitacorapro/api-types";
import {
  ESTADO_COLUMNS,
  columnOf,
  entryState,
  type EstadoColumnKey,
} from "../postventa/entryThread";
import { normalizeSearch, storeTitle } from "../util";
import {
  applyFilters as applyListFilters,
  clearFilters,
  parseListState as parseList,
  writeListState as writeList,
  type ApplyOptions,
  type ListConfig,
  type ListPatch,
  type ListState,
} from "./listState";

// Severity slug for the facility recipe's top severity. Hardcoded since
// v1 of the overview — when another vertical consumes these screens,
// resolve it from the recipe's severities by rank instead.
export const CRIT_SEVERITY = "critica";

/** Fallback Criticidad domain while the recipe is unknown. */
export const SEVERIDADES_FALLBACK: readonly string[] = ["critica", "moderada", "leve"];

/** The `proveedor` value of a row nobody holds. */
export const SIN_PROVEEDOR = "sin";

/** The chip label of a `?tienda=` that no row carries: never the UUID. */
export const TIENDA_SELECCIONADA = "Tienda seleccionada";

export type Plazo = "sin_asignar" | "vencido" | "en_plazo" | "sin_plazo" | "resuelta" | "cerrada";
export const PLAZOS: readonly Plazo[] = [
  "sin_asignar",
  "vencido",
  "en_plazo",
  "sin_plazo",
  "resuelta",
  "cerrada",
];

/** «Sin atender»: nobody holds it in this cycle, or its assignee is past
 *  the plazo the assignment stamped. */
export const SIN_ATENDER_PLAZOS: readonly Plazo[] = ["sin_asignar", "vencido"];

// A lookup rather than a switch: the plazo value «resuelta» shares its
// spelling with a postventa phase, and a `case` on it reads as a phase
// comparison to the observation-phase-single-source fitness check.
const PLAZO_LABEL: Record<Exclude<Plazo, "sin_asignar">, string> = {
  vencido: "Plazo vencido",
  en_plazo: "En plazo",
  sin_plazo: "Sin plazo",
  resuelta: "Resuelta",
  cerrada: "Cerrada",
};

export function plazoLabel(p: Plazo, assigneeLower: string): string {
  return p === "sin_asignar" ? `Sin ${assigneeLower}` : PLAZO_LABEL[p];
}

type PlazoEntry = Pick<
  FacilityDashboardEntry,
  | "observationStatus"
  | "observationPhase"
  | "reopenCount"
  | "observationClosedAt"
  | "observationClosedReason"
  | "observationDueAt"
>;

/** Where an entry stands against its plazo, with `nowMs` injected. Due is
 *  compared instant to instant (strict: due exactly now is still en
 *  plazo), the same comparison the overview's tile always made. An
 *  unreadable due date counts as no due date. */
export function plazoOf(
  e: PlazoEntry,
  vocab: ObservationPhaseVocabulary | undefined,
  nowMs: number
): Plazo {
  const { kind } = entryState(e, vocab);
  switch (kind) {
    // «appealed» es sólo de postventa (ADR-0141): un rechazo que el
    // propietario apeló espera la decisión del jefe, como una sin asignar.
    case "open":
    case "reopened":
    case "accepted":
    case "appealed":
      return "sin_asignar";
    case "assigned": {
      if (e.observationDueAt == null) return "sin_plazo";
      const due = new Date(e.observationDueAt).getTime();
      if (Number.isNaN(due)) return "sin_plazo";
      return due < nowMs ? "vencido" : "en_plazo";
    }
    case "resolved":
      return "resuelta";
    // «rejected_closed» y «closed_no_response» son terminales sólo de
    // postventa (ADR-0141, ADR-0142).
    case "conforme":
    case "rejected":
    case "rejected_closed":
    case "closed_no_response":
      return "cerrada";
  }
}

/** ADR-0134 — a critical observation needs attention while nobody holds
 *  it in this cycle (open / reopened / accepted), or while its assignee is
 *  past the plazo. Resolved-awaiting-review is not «sin atender»: the work
 *  came back. */
export function isCritSinAtender(
  e: PlazoEntry & Pick<FacilityDashboardEntry, "severity">,
  vocab: ObservationPhaseVocabulary | undefined,
  nowMs: number
): boolean {
  return e.severity === CRIT_SEVERITY && SIN_ATENDER_PLAZOS.includes(plazoOf(e, vocab, nowMs));
}

/** The Criticidad domain: the recipe's defect severities, most severe
 *  first. A positive level («Conforme») is not an observation severity. */
export function severityDomain(recipe: Pick<RecipeMeResponse, "severities"> | undefined): string[] {
  const sevs = recipe?.severities?.filter((s) => s.conformance !== "positive") ?? [];
  if (sevs.length === 0) return [...SEVERIDADES_FALLBACK];
  return [...sevs].sort((a, b) => b.rank - a.rank).map((s) => s.key);
}

export const ESTADOS: readonly EstadoColumnKey[] = ESTADO_COLUMNS.map((c) => c.key);

export type FilterKey = "tienda" | "severidad" | "estado" | "proveedor" | "plazo" | "marca";
export const FILTER_KEYS: readonly FilterKey[] = [
  "tienda",
  "severidad",
  "estado",
  "proveedor",
  "plazo",
  "marca",
];

export function listConfig(
  severities: readonly string[] = SEVERIDADES_FALLBACK
): ListConfig<FilterKey> {
  return {
    keys: FILTER_KEYS,
    domains: { severidad: severities, estado: ESTADOS, plazo: PLAZOS },
    aliases: { tienda: "asset" },
  };
}

export type ObsState = ListState<FilterKey>;
export type ObsPatch = ListPatch<FilterKey>;

export const CLEAR_FILTERS: ObsPatch = clearFilters(FILTER_KEYS);

export type FilaObs = {
  id: string;
  e: FacilityDashboardEntry;
  asset: FacilityDashboardAsset | undefined;
  estado: EstadoColumnKey;
  plazo: Plazo;
  haystack: string;
};

export function buildFilas(
  entries: readonly FacilityDashboardEntry[],
  assetById: ReadonlyMap<string, FacilityDashboardAsset>,
  vocab: ObservationPhaseVocabulary | undefined,
  nowMs: number
): FilaObs[] {
  return entries.map((e) => {
    const asset = assetById.get(e.assetId);
    // Store identity fields (unit, group, brand) join the haystack so
    // "local 12" finds a row the way the Tiendas list would.
    const haystack = normalizeSearch(
      [
        e.humanDescription,
        e.aiDescription,
        e.element,
        e.areaName,
        e.assetAddress,
        e.assetUnit,
        e.assetGroupName,
        asset?.brandName,
      ]
        .filter(Boolean)
        .join(" ")
    );
    return {
      id: e.id,
      e,
      asset,
      estado: columnOf(entryState(e, vocab)),
      plazo: plazoOf(e, vocab, nowMs),
      haystack,
    };
  });
}

export function valueOf(f: FilaObs, k: FilterKey): string {
  switch (k) {
    case "tienda":
      return f.e.assetId;
    case "severidad":
      return f.e.severity;
    case "estado":
      return f.estado;
    case "proveedor":
      return f.e.providerId ?? SIN_PROVEEDOR;
    case "plazo":
      return f.plazo;
    case "marca":
      return f.asset?.brandId ?? "";
  }
}

export function parseListState(sp: URLSearchParams, severities?: readonly string[]): ObsState {
  return parseList(sp, listConfig(severities));
}

export function writeListState(prev: URLSearchParams, patch: ObsPatch): URLSearchParams {
  return writeList(prev, patch, listConfig());
}

/** A row the user just acted on (`keep`) ignores Estado and Plazo, so it
 *  does not vanish from under the cursor. */
export function applyFilters(
  filas: readonly FilaObs[],
  s: ObsState,
  opts: Omit<ApplyOptions<FilterKey>, "keepIgnores"> = {}
): FilaObs[] {
  return applyListFilters(filas, s, FILTER_KEYS, valueOf, {
    ...opts,
    keepIgnores: ["estado", "plazo"],
  });
}

/** The Tienda chip label of an asset. */
export function tiendaLabel(asset: FacilityDashboardAsset | undefined): string {
  return asset ? storeTitle(asset) : TIENDA_SELECCIONADA;
}

/** `/observaciones` with this filter set, canonical. */
export function observacionesHref(patch: ObsPatch): string {
  const qs = writeListState(new URLSearchParams(), patch).toString();
  return qs ? `/observaciones?${qs}` : "/observaciones";
}
